<template>
  <v-container fluid class="csharp-playground-page pa-4 pa-md-6">
    <v-card class="playground-card" elevation="2">
      <v-toolbar color="primary" class="playground-toolbar px-2 px-md-4">
        <v-icon class="mr-3" aria-hidden="true">mdi-language-csharp</v-icon>
        <v-toolbar-title class="text-h6 text-md-h5 font-weight-bold">
          C# Function Playground
        </v-toolbar-title>
        <v-spacer />
        <v-chip color="white" class="prototype-chip" variant="outlined" size="small">
          Local prototype
        </v-chip>
      </v-toolbar>

      <v-card-text class="pa-4 pa-md-6">
        <v-alert
          class="mb-6"
          type="info"
          variant="tonal"
          border="start"
          title="Prototype with local testing"
          text="Save a draft in this browser, validate the C# function through the test API, enter its inputs, and run it."
        />

        <v-row>
          <v-col cols="12" md="4">
            <div class="section-heading function-details-heading mb-3">
              <div class="text-subtitle-1 font-weight-bold">Function details</div>
              <div class="text-body-2 text-medium-emphasis">
                Describe the reusable calculation before editing its method.
              </div>
            </div>

            <v-text-field
              v-model="functionName"
              label="Function name"
              placeholder="CalculateTax"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-function-variant"
              data-testid="csharp-function-name-input"
              class="mb-3"
            />

            <v-textarea
              v-model="functionDescription"
              label="Description"
              placeholder="What should this function calculate?"
              variant="outlined"
              density="comfortable"
              rows="5"
              auto-grow
              prepend-inner-icon="mdi-text-box-outline"
              data-testid="csharp-description-input"
              class="mb-4"
            />

            <v-card class="contract-card" variant="outlined">
              <v-card-item>
                <template #prepend>
                  <v-icon color="primary" aria-hidden="true">mdi-file-code-outline</v-icon>
                </template>
                <v-card-title class="text-subtitle-1">Method definition</v-card-title>
                <v-card-subtitle>Complete C# method</v-card-subtitle>
              </v-card-item>

              <v-card-text class="pt-0">
                <code class="method-preview">
                  {{ functionName || 'FunctionName' }}(parameters)
                </code>
                <div class="text-caption text-medium-emphasis mt-3">
                  The function signature and body are edited together in the code editor.
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="8">
            <div class="section-heading editor-heading mb-3">
              <div>
                <div class="text-subtitle-1 font-weight-bold">C# code</div>
                <div class="text-body-2 text-medium-emphasis">
                  Write a complete static class with a public static <code>Run</code> method.
                </div>
              </div>
              <v-chip size="small" color="success" variant="tonal">C#</v-chip>
            </div>

            <div class="code-editor-shell">
              <div class="code-editor-toolbar">
                <div class="d-flex align-center ga-2">
                  <span class="editor-status-dot" aria-hidden="true" />
                  <span class="text-caption font-weight-medium">Method.cs</span>
                </div>
                <div class="d-flex align-center ga-1">
                  <span class="text-caption editor-toolbar-muted">Editing mode</span>

                  <v-tooltip text="Reset code" location="top">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-restore"
                        variant="text"
                        size="small"
                        class="editor-toolbar-action"
                        aria-label="Reset code"
                        @click="resetCode"
                      />
                    </template>
                  </v-tooltip>

                  <v-tooltip :text="copied ? 'Code copied' : 'Copy code'" location="top">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-content-copy"
                        variant="text"
                        size="small"
                        class="editor-toolbar-action"
                        :disabled="!code.trim()"
                        aria-label="Copy code"
                        @click="copyCode"
                      />
                    </template>
                  </v-tooltip>
                </div>
              </div>

              <div class="code-editor-wrapper">
                <PrismEditor
                  v-model="code"
                  class="csharp-editor"
                  :highlight="highlighter"
                  :line-numbers="true"
                  data-testid="csharp-code-input"
                  aria-label="C# function code editor"
                />
              </div>
            </div>

            <div class="d-flex flex-wrap align-center justify-space-between ga-2 mt-4">
              <v-btn
                color="primary"
                prepend-icon="mdi-check-circle-outline"
                :loading="isValidating"
                :disabled="!functionName.trim() || !code.trim() || isRunning"
                data-testid="csharp-validate-btn"
                @click="validateFunction"
              >
                Validate
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-content-save-outline"
                data-testid="csharp-save-local-btn"
                @click="saveLocally"
              >
                Save locally
              </v-btn>
            </div>

            <div v-if="saveMessage" class="mt-3 text-right" role="status" data-testid="csharp-status">
              <span class="text-body-2 text-success">
                <v-icon size="18" class="mr-1" aria-hidden="true">mdi-check-circle-outline</v-icon>
                {{ saveMessage }}
              </span>
            </div>

            <v-alert
              v-if="apiError"
              class="mt-3"
              type="error"
              variant="tonal"
              border="start"
              title="Function request failed"
              :text="apiError"
              data-testid="csharp-error"
            />
          </v-col>
        </v-row>

        <v-row v-if="hasValidated" class="mt-2">
          <v-col cols="12" md="7">
            <v-card class="parameter-card" variant="outlined" data-testid="csharp-parameters-card">
              <v-card-item>
                <template #prepend>
                  <v-icon color="primary" aria-hidden="true">mdi-tune-variant</v-icon>
                </template>
                <v-card-title class="text-subtitle-1">Function inputs</v-card-title>
                <v-card-subtitle>Enter values for the validated C# parameters.</v-card-subtitle>
              </v-card-item>

              <v-card-text>
                <div v-if="parameters.length" class="d-flex flex-column ga-2">
                  <template v-for="parameter in parameters" :key="parameter.name">
                    <v-checkbox
                      v-if="parameter.type === 'Boolean'"
                      v-model="parameter.value"
                      :label="parameterLabel(parameter)"
                      :data-testid="`csharp-parameter-${parameter.name}`"
                      color="primary"
                      hide-details
                    />
                    <v-text-field
                      v-else
                      v-model="parameter.value"
                      :label="parameterLabel(parameter)"
                      :type="parameterInputType(parameter)"
                      :data-testid="`csharp-parameter-${parameter.name}`"
                      variant="outlined"
                      density="comfortable"
                      :hint="parameterHint(parameter)"
                      persistent-hint
                    />
                  </template>
                </div>
                <div v-else class="text-body-2 text-medium-emphasis">
                  This function does not require any inputs.
                </div>
              </v-card-text>

              <v-card-actions class="pt-0 justify-end">
                <v-btn
                  color="primary"
                  variant="elevated"
                  prepend-icon="mdi-play-circle-outline"
                  :loading="isRunning"
                  :disabled="!hasValidated || isValidating"
                  data-testid="csharp-run-btn"
                  @click="runFunction"
                >
                  Run function
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <v-card class="result-card" variant="outlined" data-testid="csharp-result">
              <v-card-item>
                <template #prepend>
                  <v-icon color="success" aria-hidden="true">mdi-calculator-variant-outline</v-icon>
                </template>
                <v-card-title class="text-subtitle-1">Execution result</v-card-title>
                <v-card-subtitle>The value returned by <code>Run</code>.</v-card-subtitle>
              </v-card-item>

              <v-card-text>
                <pre v-if="result !== null && result !== undefined" class="result-pre">{{ formattedResult }}</pre>
                <div v-else class="text-body-2 text-medium-emphasis">
                  Run the function to see its result here.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-csharp';
import 'prismjs/themes/prism-tomorrow.css';
import csharpFunctionApi from '@/services/csharpFunctionApi';

export default {
  name: 'CSharpPlayground',
  components: {
    PrismEditor,
  },
  data() {
    const defaultFunctionName = 'CalculateTax';
    const defaultFunctionDescription = 'Calculate the tax amount for a given base amount.';
    const defaultCode = `public static class CalculateTax
{
    public static decimal Run(decimal amount, decimal taxRate = 0.07m)
    {
        return amount * taxRate;
    }
}`;

    return {
      functionName: defaultFunctionName,
      functionDescription: defaultFunctionDescription,
      defaultFunctionName,
      defaultFunctionDescription,
      code: defaultCode,
      defaultCode,
      saveMessage: '',
      copied: false,
      copyResetTimer: null,
      localStorageKey: 'csharpFunctionPlaygroundDraft',
      parameters: [],
      result: null,
      isValidating: false,
      isRunning: false,
      hasValidated: false,
      apiError: '',
    };
  },
  computed: {
    formattedResult() {
      if (this.result === null || this.result === undefined) {
        return '';
      }

      return typeof this.result === 'object'
        ? JSON.stringify(this.result, null, 2)
        : String(this.result);
    },
  },
  watch: {
    functionName() {
      this.invalidateRuntimeState();
    },
    code() {
      this.invalidateRuntimeState();
    },
  },
  created() {
    this.loadDraft();
  },
  beforeUnmount() {
    if (this.copyResetTimer) {
      window.clearTimeout(this.copyResetTimer);
    }
  },
  methods: {
    highlighter(code) {
      return highlight(code, languages.csharp);
    },
    loadDraft() {
      const storedDraft = localStorage.getItem(this.localStorageKey);

      if (!storedDraft) {
        return;
      }

      try {
        const draft = JSON.parse(storedDraft);

        if (
          typeof draft.name === 'string' &&
          typeof draft.description === 'string' &&
          typeof draft.sourceCode === 'string'
        ) {
          this.functionName = draft.name;
          this.functionDescription = draft.description;
          this.code = draft.sourceCode;
        }
      } catch {
        localStorage.removeItem(this.localStorageKey);
      }
    },
    saveLocally() {
      localStorage.setItem(this.localStorageKey, JSON.stringify({
        name: this.functionName.trim(),
        description: this.functionDescription.trim(),
        sourceCode: this.code,
      }));
      this.saveMessage = 'Function saved locally in this browser.';
    },
    invalidateRuntimeState() {
      this.parameters = [];
      this.result = null;
      this.apiError = '';
      this.hasValidated = false;
    },
    normalizeParameter(parameter) {
      const name = parameter.name ?? parameter.Name;
      const type = parameter.type ?? parameter.Type ?? 'String';
      const required = parameter.required ?? parameter.Required ?? false;
      const defaultValue = parameter.default ?? parameter.Default ?? null;

      return {
        name,
        type: String(type).split('.').pop(),
        required: Boolean(required),
        default: defaultValue,
        value: defaultValue === null || defaultValue === undefined ? '' : defaultValue,
      };
    },
    async validateFunction() {
      const name = this.functionName.trim();
      const sourceCode = this.code.trim();

      if (!name || !sourceCode) {
        this.apiError = 'A function name and C# source code are required.';
        return;
      }

      this.isValidating = true;
      this.apiError = '';
      this.parameters = [];
      this.result = null;
      this.hasValidated = false;
      this.saveMessage = '';

      try {
        const response = await csharpFunctionApi.validate({ name, sourceCode });
        const responseParameters = response.parameters ?? response.Parameters ?? [];

        this.parameters = responseParameters.map(parameter => this.normalizeParameter(parameter));
        this.hasValidated = true;
        this.saveMessage = 'Function validated. Enter values and run it.';
      } catch (error) {
        this.apiError = error.message;
      } finally {
        this.isValidating = false;
      }
    },
    isNumericType(parameter) {
      return /^(Byte|Int16|Int32|Int64|Single|Double|Decimal)$/.test(parameter.type);
    },
    parameterInputType(parameter) {
      return this.isNumericType(parameter) ? 'number' : 'text';
    },
    parameterLabel(parameter) {
      return `${parameter.name}${parameter.required ? ' (required)' : ''}`;
    },
    parameterHint(parameter) {
      if (parameter.default === null || parameter.default === undefined) {
        return parameter.type;
      }

      return `${parameter.type}, default ${parameter.default}`;
    },
    coerceParameterValue(parameter) {
      const value = parameter.value;

      if (value === '' || value === null || value === undefined) {
        if (parameter.required) {
          throw new Error(`Required parameter '${parameter.name}' is missing.`);
        }

        return parameter.default ?? null;
      }

      if (parameter.type === 'Boolean') {
        return value === true || value === 'true' || value === '1' || value === 1;
      }

      if (this.isNumericType(parameter)) {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
          throw new Error(`Parameter '${parameter.name}' must be a valid ${parameter.type}.`);
        }

        return numericValue;
      }

      return String(value);
    },
    async runFunction() {
      if (!this.hasValidated) {
        return;
      }

      let argumentsPayload;

      try {
        argumentsPayload = Object.fromEntries(
          this.parameters.map(parameter => [parameter.name, this.coerceParameterValue(parameter)]),
        );
      } catch (error) {
        this.apiError = error.message;
        return;
      }

      this.isRunning = true;
      this.apiError = '';

      try {
        const response = await csharpFunctionApi.run({
          name: this.functionName.trim(),
          sourceCode: this.code,
          arguments: argumentsPayload,
        });

        this.result = response.result ?? response.Result;
        this.saveMessage = 'Function executed successfully.';
      } catch (error) {
        this.apiError = error.message;
      } finally {
        this.isRunning = false;
      }
    },
    resetCode() {
      this.functionName = this.defaultFunctionName;
      this.functionDescription = this.defaultFunctionDescription;
      this.code = this.defaultCode;
      localStorage.removeItem(this.localStorageKey);
      this.saveMessage = 'Function reset to the sample definition.';
    },
    async copyCode() {
      if (!this.code.trim()) {
        return;
      }

      if (!navigator.clipboard?.writeText) {
        this.saveMessage = 'Clipboard access is not available in this browser.';
        return;
      }

      try {
        await navigator.clipboard.writeText(this.code);
        this.copied = true;
        this.saveMessage = 'Function copied to the clipboard.';
        this.copyResetTimer = window.setTimeout(() => {
          this.copied = false;
        }, 1800);
      } catch {
        this.saveMessage = 'The function could not be copied.';
      }
    },
  },
};
</script>

<style scoped>
.csharp-playground-page {
  min-height: calc(100vh - 88px);
  background: #f5f7fb;
}

.playground-card {
  max-width: 1440px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.playground-toolbar {
  min-height: 68px;
}

.prototype-chip {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.65) !important;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.function-details-heading {
  flex-direction: column;
  gap: 4px;
}

.contract-card {
  background: #f8fafc;
}

.parameter-card,
.result-card {
  height: 100%;
  background: #f8fafc;
}

.result-pre {
  min-height: 96px;
  margin: 0;
  padding: 14px;
  overflow-x: auto;
  color: #e2e8f0;
  background: #0f172a;
  border-radius: 6px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.method-preview {
  display: block;
  padding: 12px 14px;
  overflow-x: auto;
  color: #334155;
  background: #e2e8f0;
  border-radius: 6px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.82rem;
  white-space: nowrap;
}

.code-editor-shell {
  overflow: hidden;
  border: 1px solid #334155;
  border-radius: 10px;
  background: #0f172a;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
}

.code-editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  padding: 0 14px;
  color: #e2e8f0;
  background: #1e293b;
  border-bottom: 1px solid #334155;
}

.editor-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.8);
}

.editor-toolbar-muted {
  color: #94a3b8;
}

.editor-toolbar-action {
  color: #cbd5e1 !important;
}

.editor-toolbar-action:hover {
  color: #ffffff !important;
  background: rgba(148, 163, 184, 0.18);
}

.code-editor-wrapper {
  min-height: 360px;
  padding: 12px 0;
}

.csharp-editor {
  min-height: 336px;
  color: #f8fafc;
  background: #0f172a;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
  line-height: 1.65;
}

.csharp-editor :deep(textarea) {
  min-height: 336px;
  color: #f8fafc;
  caret-color: #22c55e;
}

.csharp-editor :deep(.prism-editor__textarea:focus) {
  outline: 2px solid rgba(34, 197, 94, 0.7);
  outline-offset: -2px;
}

.csharp-editor :deep(.prism-editor__line-numbers) {
  color: #64748b;
  background: #111827;
  border-right: 1px solid #334155;
}

@media (max-width: 600px) {
  .csharp-playground-page {
    min-height: 100vh;
  }

  .playground-toolbar {
    min-height: 60px;
  }

  .prototype-chip {
    display: none;
  }

  .code-editor-wrapper {
    min-height: 300px;
  }

  .csharp-editor,
  .csharp-editor :deep(textarea) {
    min-height: 276px;
    font-size: 0.8rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
